<template>
  <div class="cplan-period">
    <div class="d-flex">
      <div class="cplan-period__head flex-grow-1">
        {{ teachingPeriodTitle }}
        <p v-if="!hasUnits">No units allocated to this teaching period.</p>
      </div>
      <div class="align-items-flex-end d-flex align-items-start">
        <div class="cplan-period__max-units position-relative">
          <button type="button" class="btn-clear" v-on:click="toggleMaxCreditControl" ref="maxUnitToggle">
            <span v-if="loading"><i class="far fa-spinner-third fa-spin"></i></span>
            <span v-if="!loading">{{ teachingPeriod.workload }}</span> credit points <i class="far fa-sliders-h"></i>
          </button>
          <div class="cplan-period__max-units-panel" v-if="showMaxCreditControl" v-click-outside="closeMaxCreditControl">
            <div class="d-flex flex-row align-items-center">
              <div class="cplan-period__max-units-panel-left">
                How many credit points would you like to study?
              </div>
              <div class="cplan-period__max-units-panel-controls">
                <button type="button" class="btn-icon-round" v-on:click="incUnitDown" :disabled="loading || reduceTeachingPeriodDisabled()"><i
                    class="far fa-minus"></i></button>
                <span v-if="loading"><i class="far fa-spinner-third fa-spin"></i></span>
                <span v-if="!loading">{{ teachingPeriod.workload }}</span>
                <button type="button" class="btn-icon-round" v-on:click="incUnitUp" :disabled="loading || increaseTeachingPeriodDisabled()"><i
                    class="far fa-plus"></i></button>
              </div>
            </div>
            <div class="w-100 page-alert slimline mt-2" v-if="!loading && displayCreditsError">
              <span class="" :id="`tooltip-prereq-alert-${teachingPeriod.teachingScheduled}`">
                <i class="far fa-lock mr-1"></i>You have locked some units. You cannot further reduce the study load to less than the total credit points for
                locked units.
              </span>
            </div>
            <transition name="pop">
              <div class="w-100 page-alert danger slimline mt-2" v-if="displayPreReqError">
                <span class="" :id="`tooltip-prereq-alert-${teachingPeriod.teachingScheduled}`">
                  <i class="far fa-exclamation-circle"></i> Error reducing workload, Prerequsites cannot be met.
                </span>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <div class="cplan-period__body">
      <div v-show="hasUnits" class="w-100">
        <div class="d-flex flex-wrap">
          <template v-for="unit in teachingPeriod.units">
            <PlanUnitCard :key="unit.code" :unit="unit" :year="year" :startYear="startYear" :teachingPeriod="teachingPeriod.teachingScheduled" />
          </template>
          <template v-for="emptyUnit in placeholderCards">
            <div class="cplan-unit-card-outer empty print-hide" :key="emptyUnit">
              <div class="cplan-unit-card-empty"></div>
            </div>
          </template>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside'
import PlanUnitCard from './PlanUnitCard.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  props: {
    teachingPeriod: { type: Object, required: true },
    teachingPeriodTitle: { type: String, required: true },
    year: { type: Number, required: true },
    startYear: { type: Number, required: true }
  },
  data() {
    return {
      showMaxCreditControl: false,
      displayPreReqError: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters({
      getLockedUnits: 'plans/getLockedUnits'
    }),
    scheduledCreditPoints() {
      let total = 0
      this.teachingPeriod.units.forEach((unit) => (total += unit.creditPoints))
      return total
    },
    teachingPeriodFormatted() {
      return this.teachingPeriodTitle + '-' + this.teachingPeriod.teachingScheduled
    },
    placeholderCards() {
      var periodCredits = 0
      this.teachingPeriod.units.forEach((unit) => (periodCredits = periodCredits + unit.creditPoints))
      var emptySlots = Math.floor((this.teachingPeriod.workload - periodCredits) / 3)
      return emptySlots > 0 ? emptySlots : null
    },
    lockedUnits() {
      return this.teachingPeriod.units.filter((unit) => this.getLockedUnits.some((lockedUnit) => lockedUnit.code === unit.code))
    },
    lockedUnitCreditPoints() {
      let lockedCreditPoints = 0
      this.lockedUnits.forEach((lockedUnit) => (lockedCreditPoints += lockedUnit.creditPoints))
      return lockedCreditPoints
    },
    displayCreditsError() {
      return this.lockedUnitCreditPoints != 0 && this.teachingPeriod.workload <= this.lockedUnitCreditPoints
    },
    hasUnits() {
      return this.teachingPeriod.units.length > 0
    }
  },
  methods: {
    ...mapActions({
      generateCoursePlan: 'plans/generateCoursePlan'
    }),
    reduceTeachingPeriodDisabled() {
      return this.teachingPeriod?.workload <= 0 || this.teachingPeriod?.workload <= this.lockedUnitCreditPoints || false
    },
    increaseTeachingPeriodDisabled() {
      return this.teachingPeriod?.workload >= 18 || false
    },
    toggleMaxCreditControl() {
      this.showMaxCreditControl = !this.showMaxCreditControl
      if (this.showMaxCreditControl == false) {
        this.refreshUnits()
      }
    },
    closeMaxCreditControl() {
      this.showMaxCreditControl = false
      this.refreshUnits()
    },
    async incUnitDown() {
      this.loading = true
      this.teachingPeriod.workload = this.teachingPeriod.workload - 3
      try {
        await this.generateCoursePlan({ doForceGenerate:true, throwErrorOnNewErrors:true })
      } catch (exception) {
        this.teachingPeriod.workload = this.teachingPeriod.workload + 3
        setTimeout(() => {
          this.displayPreReqError = true
        }, 0)
        setTimeout(() => {
          this.displayPreReqError = false
        }, 4000)
      }
      this.loading = false
    },
    async incUnitUp() {
      this.loading = true
      this.teachingPeriod.workload = this.teachingPeriod.workload + 3
      try {
        await this.generateCoursePlan({ doForceGenerate:true, throwErrorOnNewErrors:true })
      } catch (exception) {
        this.teachingPeriod.workload = this.teachingPeriod.workload - 3
        setTimeout(() => {
          this.displayPreReqError = true
        }, 0)
        setTimeout(() => {
          this.displayPreReqError = false
        }, 4000)
      }
      this.loading = false
    },
    refreshUnits() {
      // Max unit parameter should be sent on panel close (not each time the it changes)
    }
  },
  mounted() {
    // Prevent click outside event with popupItem
    this.popupItem = this.$refs.maxUnitToggle
  },
  directives: {
    ClickOutside
  },
  components: { PlanUnitCard }
}
</script>

